'use client';

import { FC, useEffect, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useTranslations } from '@/hooks/useTranslations';
import { EditClientForm } from './edit-client-form';
import { IClient } from '@/types/client';
import { useClientContext } from '@/contexts/client-context';
import { getClients } from '@/actions/client/client-queries';

export const EditClientPopup: FC = () => {
  const { dispatch, state } = useClientContext();

  const t = useTranslations('CreateClientPopup');

  const [clients, setClients] = useState<IClient[]>([]);
  const [loading, setLoading] = useState(true);

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen && state.clientId) {
      dispatch({
        type: 'OPEN_EDIT_CLIENT_POPUP',
        payload: { clientId: state.clientId },
      });
    } else {
      dispatch({
        type: 'CLOSE_EDIT_CLIENT_POPUP',
      });
    }
  };

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const data = await getClients();
        setClients(Array.isArray(data.clients) ? data.clients : []);
      } catch (error) {
        console.error('Error fetching clients:', error);
        setClients([]);
      } finally {
        setLoading(false);
      }
    };

    if (state.isEditClientPopupOpen) {
      setLoading(true);
      fetchClients();
    }
  }, [state.isEditClientPopupOpen]);

  const client = clients.find((item) => item.id === state.clientId);

  if (loading) {
    return (
      <Sheet open={state.isEditClientPopupOpen} onOpenChange={handleOpenChange}>
        <SheetContent className="bg-white dark:bg-black">
          <SheetHeader>
            <SheetTitle>{t('editClientTitle')}</SheetTitle>
            <SheetDescription>Loading client details...</SheetDescription>
          </SheetHeader>
          <div className="flex items-center justify-center p-8">
            <div className="text-sm text-muted-foreground">Loading...</div>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  if (!client) {
    return (
      <Sheet open={state.isEditClientPopupOpen} onOpenChange={handleOpenChange}>
        <SheetContent className="bg-white dark:bg-black">
          <SheetHeader>
            <SheetTitle>{t('editClientTitle')}</SheetTitle>
            <SheetDescription>Client could not be found.</SheetDescription>
          </SheetHeader>
          <div className="flex items-center justify-center p-8">
            <div className="text-sm text-destructive">
              Error: Client not found
            </div>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Sheet open={state.isEditClientPopupOpen} onOpenChange={handleOpenChange}>
      <SheetContent className="bg-white dark:bg-black">
        <SheetHeader className="text-left">
          <SheetTitle>{t('editClientTitle')}</SheetTitle>
          <SheetDescription>{t('editClientDescription')}</SheetDescription>
        </SheetHeader>

        <div className="grid gap-4 p-4">
          <EditClientForm client={client} />
        </div>
      </SheetContent>
    </Sheet>
  );
};
